/**
 * 文件上传API模块
 * 支持图片、视频、音频上传到云存储（OSS/CDN）
 * 
 * 实现方式：
 * - 方案A：通过后端中转上传（简单，推荐开发环境）
 * - 方案B：前端直传OSS（推荐生产环境）
 * - 方案C：使用OSS SDK（功能最完善）
 */

import { request, getStoredToken, getCurrentTenantId, getCurrentEnterpriseId } from './client';

/**
 * 文件类型定义
 */
export const FILE_TYPES = {
  IMAGE: 'image',
  VIDEO: 'video',
  AUDIO: 'audio',
  ATTACHMENT: 'attachment'
};

/**
 * 文件大小限制（字节）
 */
export const FILE_SIZE_LIMITS = {
  IMAGE: 5 * 1024 * 1024,      // 5MB
  VIDEO: 100 * 1024 * 1024,    // 100MB
  AUDIO: 20 * 1024 * 1024,     // 20MB
  ATTACHMENT: 10 * 1024 * 1024 // 10MB
};

/**
 * 允许的文件扩展名
 */
export const ALLOWED_EXTENSIONS = {
  IMAGE: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'],
  VIDEO: ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.mkv', '.webm'],
  AUDIO: ['.mp3', '.wav', '.aac', '.flac', '.ogg', '.m4a'],
  ATTACHMENT: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt']
};

/**
 * 验证文件
 */
export const validateFile = (file, fileType) => {
  if (!file) {
    throw new Error('请选择文件');
  }

  // 检查文件大小
  const sizeLimit = FILE_SIZE_LIMITS[fileType.toUpperCase()];
  if (file.size > sizeLimit) {
    throw new Error(`文件大小超过限制（最大${(sizeLimit / 1024 / 1024).toFixed(0)}MB）`);
  }

  // 检查文件扩展名
  const fileName = file.name.toLowerCase();
  const allowedExts = ALLOWED_EXTENSIONS[fileType.toUpperCase()];
  const isValidExt = allowedExts.some(ext => fileName.endsWith(ext));
  
  if (!isValidExt) {
    throw new Error(`不支持的文件格式，请上传：${allowedExts.join(', ')}`);
  }

  return true;
};

/**
 * 生成唯一文件名
 */
const generateFileName = (file, folder = 'labels') => {
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 8);
  const ext = file.name.substring(file.name.lastIndexOf('.'));
  return `${folder}/${timestamp}_${randomStr}${ext}`;
};

/**
 * 方案A：通过后端中转上传（推荐，简单易用）
 * 
 * @param {File} file - 要上传的文件
 * @param {Object} options - 上传选项
 * @param {string} options.fileType - 文件类型 (image/video/audio/attachment)
 * @param {string} options.folder - 存储文件夹
 * @param {Function} options.onProgress - 进度回调函数
 * @returns {Promise<string>} CDN URL
 */
export const uploadFile = async (file, options = {}) => {
  const {
    fileType = FILE_TYPES.IMAGE,
    folder = 'labels',
    onProgress,
    labelId  // 关联的标签ID（可选）
  } = options;

  // 验证文件
  validateFile(file, fileType);

  // 创建FormData
  const formData = new FormData();
  formData.append('file', file);
  // 后端接口接受 file 和 labelId 参数，fileType 和 folder 由后端自动判断
  if (labelId) {
    formData.append('labelId', labelId);
  }

  try {
    // 使用XMLHttpRequest支持进度回调
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // 监听上传进度
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const percent = Math.round((e.loaded * 100) / e.total);
          onProgress(percent);
        }
      });

      // 监听完成
      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          try {
            const response = JSON.parse(xhr.responseText);
            // 适配后端返回格式：{code: "200", msg: "上传成功", data: {fileUrl: "...", ...}}
            if (response.code === '200' && response.data) {
              // 从LabelFile对象中获取fileUrl
              const fileUrl = response.data.fileUrl || response.data.url;
              if (fileUrl) {
                resolve(fileUrl);
              } else {
                reject(new Error('服务器返回的数据格式不正确'));
              }
            } else {
              // 兼容旧格式：{success: true, data: {url: "..."}}
              if (response.success && response.data?.url) {
                resolve(response.data.url);
              } else {
                reject(new Error(response.msg || response.message || '上传失败'));
              }
            }
          } catch (error) {
            reject(new Error('解析响应失败: ' + error.message));
          }
        } else {
          reject(new Error(`上传失败：HTTP ${xhr.status}`));
        }
      });

      // 监听错误
      xhr.addEventListener('error', () => {
        reject(new Error('网络错误，上传失败'));
      });

      // 监听超时
      xhr.addEventListener('timeout', () => {
        reject(new Error('上传超时，请重试'));
      });

      // 配置请求
      // 使用统一的Token获取方式
      const token = getStoredToken();
      // 修正文件上传路径：使用FoodLabel的文件上传接口
      xhr.open('POST', '/api/foodlabel/files/upload');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      // 与标签保存、其他上传接口使用相同的租户和企业上下文。
      const tenantId = getCurrentTenantId();
      const enterpriseId = getCurrentEnterpriseId();
      if (tenantId) {
        xhr.setRequestHeader('X-Tenant-Id', tenantId);
      }
      if (enterpriseId) {
        xhr.setRequestHeader('X-Enterprise-Id', enterpriseId);
      }
      xhr.timeout = 300000; // 5分钟超时

      // 发送请求
      xhr.send(formData);
    });
  } catch (error) {
    console.error('上传文件失败:', error);
    throw error;
  }
};

/**
 * 批量上传文件
 * 
 * @param {File[]} files - 文件数组
 * @param {Object} options - 上传选项
 * @returns {Promise<string[]>} CDN URL数组
 */
export const uploadFiles = async (files, options = {}) => {
  const {
    fileType = FILE_TYPES.IMAGE,
    folder = 'labels',
    onProgress,
    onEachProgress
  } = options;

  const totalFiles = files.length;
  const uploadedUrls = [];
  let completedFiles = 0;

  // 逐个上传（避免并发过多）
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    
    try {
      const url = await uploadFile(file, {
        fileType,
        folder,
        onProgress: (percent) => {
          // 单个文件进度
          if (onEachProgress) {
            onEachProgress(i, percent);
          }
          
          // 总体进度
          if (onProgress) {
            const totalPercent = Math.round(
              ((completedFiles + percent / 100) / totalFiles) * 100
            );
            onProgress(totalPercent);
          }
        }
      });
      
      uploadedUrls.push(url);
      completedFiles++;
      
      // 更新总体进度
      if (onProgress) {
        const totalPercent = Math.round((completedFiles / totalFiles) * 100);
        onProgress(totalPercent);
      }
    } catch (error) {
      console.error(`文件 ${file.name} 上传失败:`, error);
      throw new Error(`文件 ${file.name} 上传失败：${error.message}`);
    }
  }

  return uploadedUrls;
};

/**
 * 方案B：获取OSS直传签名（推荐生产环境）
 * 前端可以直接上传到OSS，不经过后端，节省带宽
 */
export const getOSSSignature = async (fileType = FILE_TYPES.IMAGE) => {
  try {
    const response = await request('/upload/signature', {
      method: 'GET',
      params: { fileType }
    });
    return response.data;
  } catch (error) {
    console.error('获取OSS签名失败:', error);
    throw error;
  }
};

/**
 * 方案B：直接上传到OSS
 */
export const uploadToOSS = async (file, options = {}) => {
  const {
    fileType = FILE_TYPES.IMAGE,
    folder = 'labels',
    onProgress
  } = options;

  // 验证文件
  validateFile(file, fileType);

  try {
    // 1. 获取OSS签名
    const signature = await getOSSSignature(fileType);
    const { ossUrl, accessKeyId, policy, signatureStr, key } = signature;

    // 2. 构建上传表单
    const formData = new FormData();
    const fileName = generateFileName(file, folder);
    
    formData.append('key', fileName);
    formData.append('policy', policy);
    formData.append('OSSAccessKeyId', accessKeyId);
    formData.append('signature', signatureStr);
    formData.append('success_action_status', '200');
    formData.append('file', file);

    // 3. 直接上传到OSS
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const percent = Math.round((e.loaded * 100) / e.total);
          onProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          // 返回CDN URL
          const cdnUrl = `${signature.cdnDomain}/${fileName}`;
          resolve(cdnUrl);
        } else {
          reject(new Error(`OSS上传失败：HTTP ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('OSS上传失败'));
      });

      xhr.open('POST', ossUrl);
      xhr.send(formData);
    });
  } catch (error) {
    console.error('OSS上传失败:', error);
    throw error;
  }
};

/**
 * 删除文件（调用后端API删除OSS文件）
 */
export const deleteFile = async (fileUrl) => {
  try {
    await request('/upload/file', {
      method: 'DELETE',
      params: { url: fileUrl }
    });
    return true;
  } catch (error) {
    console.error('删除文件失败:', error);
    throw error;
  }
};

/**
 * 获取文件信息
 */
export const getFileInfo = (file) => {
  return {
    name: file.name,
    size: file.size,
    type: file.type,
    sizeInMB: (file.size / 1024 / 1024).toFixed(2),
    extension: file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
  };
};

/**
 * 格式化文件大小
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

export default {
  uploadFile,
  uploadFiles,
  uploadToOSS,
  getOSSSignature,
  deleteFile,
  validateFile,
  getFileInfo,
  formatFileSize,
  FILE_TYPES,
  FILE_SIZE_LIMITS,
  ALLOWED_EXTENSIONS
};
